import { useEffect, useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Stack,
  Chip,
  TextField,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import DashboardLayout from "../layouts/DashboardLayout";
import { COLORS, SHADOW } from "../theme/tokens";
import FlatCard from "../components/FlatCard";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import TableSkeleton from "../components/TableSkeleton";
import api from "../api";
import { useUsers } from "../hooks/queries";
import { useTableSelection } from "../hooks/useTableSelection";
import SelectionActionBar from "../components/SelectionActionBar";
import AddUserModal from "../components/AddUserModal";
import { HEADING_FONT } from "../theme/fonts";

// Spec palette (blue · light) — sourced from the shared design tokens.
const BLUE = COLORS.primary;
const BLUE_SOFT = COLORS.primarySoft;

// Spec §4.5 "Module access" cell: admins get everything; a standard user's
// label counts the modules where at least one action is granted.
const ALL_MODULES = 6; // Domains, Servers, Clients, Domain Monitor, Category, Expiry Alerts
function moduleAccessLabel(user) {
  if (user.role === "admin") return "All modules";
  const perms = user.permissions || {};
  const count = Object.values(perms).filter(
    (actions) => actions && Object.values(actions).some(Boolean)
  ).length;
  if (count === 0) return "No access";
  if (count >= ALL_MODULES) return "All modules";
  return `${count} module${count === 1 ? "" : "s"}`;
}

function formatRelative(dateStr) {
  if (!dateStr) return "—";
  const then = new Date(dateStr.replace(" ", "T"));
  if (isNaN(then.getTime())) return dateStr;
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  const diffMo = Math.floor(diffD / 30);
  return `${diffMo}mo ago`;
}

function formatAbsolute(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.replace(" ", "T"));
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Turn the /users response into { rows, total, counts } for the current page.
// The backend normally returns { rows, total, counts }; if an older build
// returns a bare array, we paginate + tally on the client so the page still
// works during a partial deploy.
const EMPTY_COUNTS = { all: 0, admin: 0, user: 0 };

function normalizeUsers(data, page, pageSize) {
  if (!data) return { rows: [], total: 0, counts: EMPTY_COUNTS };
  if (Array.isArray(data)) {
    const tally = { all: data.length, admin: 0, user: 0 };
    data.forEach((u) => {
      if (u.role === "admin") tally.admin++;
      else tally.user++;
    });
    const start = (page - 1) * pageSize;
    return {
      rows: data.slice(start, start + pageSize),
      total: data.length,
      counts: tally,
    };
  }
  return {
    rows: data.rows || [],
    total: data.total || 0,
    counts: data.counts || EMPTY_COUNTS,
  };
}

function Users() {
  // Flat spec design tokens (light-only). Replaces the old glass theme
  // (useThemeColors) so the page is locked to the "Blue · Light" system.
  const colors = {
    text: COLORS.ink,
    textMuted: COLORS.muted,
    textFaint: COLORS.faint,
    cardBg: COLORS.surface,
    menuBg: COLORS.surface,
    cardBorder: COLORS.hairline,
    cardBorderHover: COLORS.borderHover,
    hover: COLORS.tile,
    divider: COLORS.hairline,
    shadowLg: SHADOW.modal,
  };
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const [search, setSearch] = useState("");
  // Debounced copy of `search` so we hit the API after the user stops typing,
  // not on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Add / Edit User modal (spec §5.4). `editUserId` null = the Add modal;
  // an id = the Edit modal for that row.
  const [addOpen, setAddOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const userModalOpen = addOpen || editUserId !== null;

  function closeUserModal() {
    setAddOpen(false);
    setEditUserId(null);
  }

  function handleUserSaved({ isEdit } = {}) {
    closeUserModal();
    queryClient.invalidateQueries({ queryKey: ["users"] });
    toast.success(isEdit ? "User updated" : "User created");
  }
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Build the query params from the active page + filters. React Query
  // re-fetches automatically whenever this object changes — and serves the
  // cache when we revisit the same combination.
  const params = useMemo(() => {
    const p = { limit: pageSize, offset: (page - 1) * pageSize };
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    if (filter !== "all") p.role = filter;
    return p;
  }, [page, pageSize, debouncedSearch, filter]);

  // placeholderData keeps the previous page on screen while the next one loads,
  // so paging/filtering never flashes an empty table. The server returns
  // { rows, total, counts }; normalizeUsers also handles a bare-array response.
  const usersQuery = useUsers(params, { placeholderData: (prev) => prev });
  const { rows, total, counts } = useMemo(
    () => normalizeUsers(usersQuery.data, page, pageSize),
    [usersQuery.data, page, pageSize]
  );
  const loading = usersQuery.isFetching;

  // Surface a load failure as a toast (matches the old behaviour).
  useEffect(() => {
    if (usersQuery.isError) {
      toast.error(usersQuery.error?.message || "Failed to load users");
    }
  }, [usersQuery.isError, usersQuery.error, toast]);

  // Debounce the search box → debouncedSearch.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  async function handleDelete(id) {
    try {
      await api.delete(`/users/${id}`);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.info("User deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    }
  }

  // --- Row selection + bulk delete ---
  // The backend always skips the logged-in admin's own id, so a bulk/all delete
  // can never lock the current user out.
  const pageIds = rows.map((u) => u.id);
  const {
    selectedIds,
    toggleRow,
    toggleAll,
    clear: clearSelection,
    allSelected,
    someSelected,
  } = useTableSelection(pageIds);
  const [bulkBusy, setBulkBusy] = useState(false);

  async function handleDeleteSelected() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} user${ids.length === 1 ? "" : "s"}?`,
      message:
        "The selected users will be permanently removed (your own account is always kept).",
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/users/bulk-delete", { ids });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? 0} user(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete selected users");
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleDeleteAll() {
    const ok = await confirm({
      title: "Delete ALL users?",
      message:
        "Every user except your own account will be permanently removed. This cannot be undone.",
      danger: true,
      confirmLabel: "Delete all",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/users/delete-all");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? 0} user(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete all users");
    } finally {
      setBulkBusy(false);
    }
  }

  // Reset to page 1 when the (debounced) search, filter, or page size changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  // If the current page falls past the end (e.g. after deleting the last row on
  // the last page), step back so we never show an empty page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = (safePage - 1) * pageSize;
  const rangeStart = total === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + pageSize, total);

  function buildPageList(current, total) {
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...pages]
      .filter((n) => n >= 1 && n <= total)
      .sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of sorted) {
      if (n - prev > 1) out.push("...");
      out.push(n);
      prev = n;
    }
    return out;
  }
  const pageList = buildPageList(safePage, totalPages);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
    },
    "& input": { color: colors.text },
  };

  // Leading 44px track is the checkbox column.
  const GRID_COLUMNS =
    "44px minmax(220px, 1.8fr) 110px minmax(140px, 1fr) minmax(140px, 1fr) 180px";

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Users
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textMuted, mt: 0.5 }}
          >
            {counts.all}{" "}
            {counts.all === 1 ? "user" : "users"} with access to the system
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ alignSelf: { xs: "flex-end", sm: "center" }, ml: { sm: "auto" } }}
        >
          {selectedIds.size > 0 && (
            <Button
              onClick={handleDeleteAll}
              startIcon={<DeleteOutlineIcon />}
              disabled={bulkBusy}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 2,
                py: 1.1,
                borderRadius: 2,
                color: "#ef4444",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
                transition: "all 0.15s ease",
              }}
            >
              Delete All
            </Button>
          )}

          <Button
            onClick={() => setAddOpen(true)}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 2.5,
              py: 1.1,
              borderRadius: 2,
              color: "#fff",
              background:
                "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
              boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
              "&:hover": {
                boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.15s ease",
            }}
          >
            Add User
          </Button>
        </Stack>
      </Stack>

      {/* Filter + Search */}
      <FlatCard
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { md: "center" },
        }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, value) => value && setFilter(value)}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              px: 2,
              py: 0.75,
              border: `1px solid ${colors.cardBorder}`,
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
              "&.Mui-selected": {
                background: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                borderColor: "#3B82F6",
                "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
              },
            },
          }}
        >
          <ToggleButton value="all">
            All
            <Chip
              size="small"
              label={counts.all}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: colors.hover,
                color: colors.textMuted,
              }}
            />
          </ToggleButton>
          <ToggleButton value="admin">
            Admins
            <Chip
              size="small"
              label={counts.admin}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(59, 130, 246, 0.15)",
                color: "#3B82F6",
              }}
            />
          </ToggleButton>
          <ToggleButton value="user">
            Users
            <Chip
              size="small"
              label={counts.user}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(245, 158, 11, 0.15)",
                color: "#f59e0b",
              }}
            />
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search by username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            ...fieldSx,
            flex: 1,
            "& .MuiOutlinedInput-root": {
              ...fieldSx["& .MuiOutlinedInput-root"],
              fontSize: 13,
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ color: colors.textFaint, mr: 1, fontSize: 18 }}
              />
            ),
          }}
        />
      </FlatCard>

      {/* Bulk-delete selection bar */}
      <SelectionActionBar
        count={selectedIds.size}
        onDelete={handleDeleteSelected}
        onClear={clearSelection}
        busy={bulkBusy}
        colors={colors}
      />

      {/* Empty state */}
      {rows.length === 0 && loading ? (
        <FlatCard sx={{ overflow: "hidden" }}>
          <TableSkeleton rows={pageSize} columns={6} />
        </FlatCard>
      ) : rows.length === 0 ? (
        <FlatCard sx={{ py: 8, textAlign: "center" }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3B82F6",
              background: "rgba(59, 130, 246, 0.12)",
              border: "1px solid rgba(59, 130, 246, 0.25)",
            }}
          >
            <PeopleAltOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: 16,
              fontWeight: 700,
              color: colors.text,
              mb: 0.5,
            }}
          >
            {counts.all === 0 ? "No users yet" : "No matches found"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
            {counts.all === 0
              ? 'Click "Add User" to create the first one'
              : "Try a different search or filter"}
          </Typography>
        </FlatCard>
      ) : (
        <FlatCard sx={{ overflow: "hidden" }}>
          {/* Horizontal scroll wrapper — full table on mobile, swipe sideways */}
          <Box sx={{ overflowX: "auto" }}>
          {/* Table header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: GRID_COLUMNS,
              minWidth: { xs: "max-content", md: "auto" },
              gap: 2,
              px: 3,
              py: 1.75,
              borderBottom: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
              fontSize: 12,
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleAll}
                sx={{ p: 0, color: colors.textMuted }}
              />
            </Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>User</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Role</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Module Access</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Created</Box>
            <Box sx={{ fontFamily: HEADING_FONT, textAlign: "center" }}>Actions</Box>
          </Box>

          {/* Rows */}
          {rows.map((user) => {
            const isAdmin = user.role === "admin";
            const roleColor = isAdmin ? "#3B82F6" : "#f59e0b";
            const roleBg = isAdmin
              ? "rgba(59, 130, 246, 0.12)"
              : "rgba(245, 158, 11, 0.12)";
            const initial = (user.username || "?").charAt(0).toUpperCase();

            return (
              <Box
                key={user.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: GRID_COLUMNS,
                  minWidth: { xs: "max-content", md: "auto" },
                  gap: 2,
                  px: 3,
                  py: 2,
                  alignItems: "center",
                  borderBottom: `1px solid ${colors.divider}`,
                  "&:last-child": { borderBottom: "none" },
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "3px",
                    background: BLUE,
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                  },
                  "&:hover": { background: COLORS.tile },
                  "&:hover::before": { opacity: 1 },
                  transition: "background 0.18s ease",
                }}
              >
                {/* Row select checkbox */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(user.id)}
                    onChange={() => toggleRow(user.id)}
                    sx={{ p: 0, color: colors.textMuted }}
                  />
                </Box>

                {/* User */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: isAdmin
                        ? "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)"
                        : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontWeight: 700,
                      fontSize: 14,
                      boxShadow: `0 4px 12px ${roleColor}55`,
                    }}
                  >
                    {initial}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontSize: 14,
                        fontWeight: 600,
                        color: colors.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.username || "(unnamed)"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: colors.textMuted,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                      }}
                    >
                      ID #{user.id}
                    </Typography>
                  </Box>
                </Stack>

                {/* Role */}
                <Chip
                  size="small"
                  icon={
                    isAdmin ? (
                      <AdminPanelSettingsOutlinedIcon
                        sx={{
                          fontSize: 14,
                          color: `${roleColor} !important`,
                        }}
                      />
                    ) : (
                      <PersonOutlineOutlinedIcon
                        sx={{
                          fontSize: 14,
                          color: `${roleColor} !important`,
                        }}
                      />
                    )
                  }
                  label={isAdmin ? "Admin" : "User"}
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontWeight: 700,
                    fontSize: 11,
                    color: roleColor,
                    background: roleBg,
                    border: `1px solid ${roleColor}33`,
                    width: "fit-content",
                    "& .MuiChip-icon": { ml: "6px" },
                  }}
                />

                {/* Module access (spec §4.5) */}
                <Box>
                  {(() => {
                    const label = moduleAccessLabel(user);
                    const full = label === "All modules";
                    return (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          px: 1.25,
                          py: 0.4,
                          borderRadius: "999px",
                          background: full ? BLUE_SOFT : colors.hover,
                          color: full ? BLUE : colors.textMuted,
                          fontFamily: HEADING_FONT,
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Box>
                    );
                  })()}
                </Box>

                {/* Created */}
                <Tooltip title={formatAbsolute(user.createdAt)}>
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontSize: 12,
                      color: colors.textMuted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatRelative(user.createdAt)}
                  </Typography>
                </Tooltip>

                {/* Actions */}
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                >
                  <Button
                    size="small"
                    startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setEditUserId(user.id)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12,
                      px: 1.25,
                      py: 0.4,
                      minWidth: 0,
                      borderRadius: 1.5,
                      color: "#3B82F6",
                      background: "rgba(59, 130, 246, 0.1)",
                      border: "1px solid rgba(59, 130, 246, 0.25)",
                      "&:hover": {
                        background: "rgba(59, 130, 246, 0.18)",
                      },
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    }
                    onClick={async () => {
                      if (
                        await confirm({
                          title: "Delete User?",
                          message: "This action cannot be undone.",
                          danger: true,
                          confirmLabel: "Delete",
                        })
                      )
                        handleDelete(user.id);
                    }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12,
                      px: 1.25,
                      py: 0.4,
                      minWidth: 0,
                      borderRadius: 1.5,
                      color: "#ef4444",
                      background: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid rgba(239, 68, 68, 0.25)",
                      "&:hover": {
                        background: "rgba(239, 68, 68, 0.18)",
                      },
                    }}
                  >
                    Delete
                  </Button>
                </Stack>
              </Box>
            );
          })}
          </Box>

          {/* Pagination footer */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderTop: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                Showing{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: colors.text }}
                >
                  {rangeStart}–{rangeEnd}
                </Box>{" "}
                of{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: colors.text }}
                >
                  {total}
                </Box>
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                MenuProps={{
                  anchorOrigin: { vertical: "top", horizontal: "left" },
                  transformOrigin: { vertical: "bottom", horizontal: "left" },
                  PaperProps: {
                    sx: {
                      mb: 0.5,
                      minWidth: 72,
                      borderRadius: 2,
                      background: colors.menuBg,
                      border: `1px solid ${colors.cardBorder}`,
                      boxShadow: colors.shadowLg,
                      "& .MuiList-root": { py: 0.5 },
                      "& .MuiMenuItem-root": {
                        fontSize: 13,
                        color: colors.text,
                        mx: 0.5,
                        my: 0.25,
                        minHeight: 32,
                        borderRadius: 1,
                        justifyContent: "center",
                        "&:hover": { background: colors.hover },
                        "&.Mui-selected": {
                          background: "rgba(59, 130, 246, 0.14)",
                          color: "#3B82F6",
                          fontWeight: 700,
                          "&:hover": { background: "rgba(59, 130, 246, 0.2)" },
                        },
                      },
                    },
                  },
                }}
                sx={{
                  mr: 0.5,
                  height: 32,
                  minWidth: 62,
                  fontSize: 13,
                  fontWeight: 700,
                  color: colors.text,
                  background: colors.cardBg,
                  borderRadius: 2,
                  transition: "border-color 0.15s ease, background 0.15s ease",
                  "& .MuiSelect-select": {
                    py: 0,
                    pl: 1.5,
                    pr: "28px !important",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorder,
                  },
                  "&:hover": { background: colors.hover },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorderHover,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3B82F6",
                    borderWidth: "1px",
                  },
                  "& .MuiSvgIcon-root": { color: colors.textMuted, right: 4 },
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>
                    {n}
                  </MenuItem>
                ))}
              </Select>

              <Tooltip title="First page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => setPage(1)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        background: colors.cardBg,
                        color: "#3B82F6",
                      },
                    }}
                  >
                    <FirstPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        background: colors.cardBg,
                        color: "#3B82F6",
                      },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {pageList.map((p, i) =>
                p === "..." ? (
                  <Box
                    key={`ellipsis-${i}`}
                    sx={{
                      px: 1,
                      fontSize: 13,
                      color: colors.textFaint,
                    }}
                  >
                    …
                  </Box>
                ) : (
                  <Box
                    key={p}
                    component="button"
                    onClick={() => setPage(p)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      px: 1,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor:
                        p === safePage ? "#3B82F6" : "transparent",
                      background:
                        p === safePage
                          ? "rgba(59, 130, 246, 0.12)"
                          : "transparent",
                      color: p === safePage ? "#3B82F6" : colors.text,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background:
                          p === safePage
                            ? "rgba(59, 130, 246, 0.18)"
                            : colors.cardBg,
                      },
                    }}
                  >
                    {p}
                  </Box>
                )
              )}

              <Tooltip title="Next">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        background: colors.cardBg,
                        color: "#3B82F6",
                      },
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Last page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() => setPage(totalPages)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        background: colors.cardBg,
                        color: "#3B82F6",
                      },
                    }}
                  >
                    <LastPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </FlatCard>
      )}

      {/* Add / Edit User modal (spec §5.4) */}
      <AddUserModal
        open={userModalOpen}
        editId={editUserId}
        onClose={closeUserModal}
        onSuccess={handleUserSaved}
      />
    </DashboardLayout>
  );
}

export default Users;
